import { keepPreviousData, useInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import { create, windowedFiniteBatchScheduler } from '@yornaath/batshit';
import isEmpty from 'lodash/isEmpty';
import { fetchTransactionRegistrationProposals } from '~/api/bookkeeping/bookkeeping-bank-transactions';
import { fetchFlow } from '~/api/flow';
import { fetchTransaction, fetchTransactions } from '~/api/transaction';
import { queryClient } from '~/client/queryClient';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { decorateWithReconciliations } from '~/services/reconciliation';
import { Company } from '~/types/company';
import { BookkeepingBankTransaction, BookkeepingBankTransactionsKeys } from '~/types/general-bookkeeping';
import { ReconciliationRelationStatus } from '~/types/reconciliation';
import { Transaction, TransactionMeta, TransactionsFiltersFields, TransactionSorting } from '~/types/transaction';
import { isDefined } from '~/types/typeGuards';
import { keyBy } from '~/utils/array';
import { isNotEmpty } from '~/utils/helpers';
import { PaginationInfo, SortInfo } from '~/utils/jsonapi-query';

const decorateWithRecommendedReconciliationFlows = async (
  transactions: BookkeepingBankTransaction[],
): Promise<BookkeepingBankTransaction[]> => {
  try {
    const transactionsWithRecommendedFlowId = transactions.filter((t) =>
      isNotEmpty(t.registrationProposal?.recommendedFlowId),
    );

    if (isEmpty(transactionsWithRecommendedFlowId)) return transactions;

    const recommendedReconciliations = transactionsWithRecommendedFlowId
      .map((t) => ({ transactionId: t.id, flowId: t.registrationProposal?.recommendedFlowId }))
      .filter((t): t is { transactionId: string; flowId: string } => isDefined(t.flowId));

    const recommendedReconciliationFlows = await Promise.all(
      recommendedReconciliations.map((r) => fetchFlow(r.flowId)),
    );

    const recommendedReconciliationFlowsDict = keyBy(recommendedReconciliationFlows, 'id');
    const recommendedReconciliationsDict = keyBy(recommendedReconciliations, 'transactionId');

    return transactions.map((t) => {
      const flowId = recommendedReconciliationsDict[t.id]?.flowId;
      return {
        ...t,
        recommendedReconciliationFlow: flowId ? recommendedReconciliationFlowsDict[flowId] : undefined,
      };
    });
  } catch {
    return transactions;
  }
};

const proposals = create({
  fetcher: fetchTransactionRegistrationProposals,
  resolver: (results, transaction) => results.find((r) => r.transactionId === transaction.id) ?? null,
  scheduler: windowedFiniteBatchScheduler({
    windowMs: 50,
    maxBatchSize: 50,
  }),
});

const decorateTransactionWithRegistrationProposal = async (
  transaction: BookkeepingBankTransaction,
  { forceRefetch = false }: { forceRefetch?: boolean } = {},
): Promise<BookkeepingBankTransaction> => {
  try {
    const proposalResponse = await queryClient.fetchQuery({
      queryKey: BookkeepingBankTransactionsKeys.registrationProposal(transaction.id),
      queryFn: () => proposals.fetch(transaction),
      staleTime: forceRefetch ? 0 : Infinity,
    });

    return {
      ...transaction,
      registrationProposal: proposalResponse?.registrationProposal,
    };
  } catch {
    return transaction;
  }
};

const decorateTransactionsWithRegistrationProposal = async (
  transactions: BookkeepingBankTransaction[],
  options?: { forceRefetch?: boolean },
): Promise<BookkeepingBankTransaction[]> => {
  return Promise.all(transactions.map((t) => decorateTransactionWithRegistrationProposal(t, options)));
};

const fetchTransactionDecorated = async (id: Transaction['id']): Promise<BookkeepingBankTransaction> => {
  const transaction = await fetchTransaction(id);
  const [withReconciliations] = await decorateWithReconciliations(
    [transaction],
    ['flow', 'flow.bookkeepingEntry', 'flow.document', 'flow.document.counterpart'],
    {
      statuses: [ReconciliationRelationStatus.VERIFIED],
    },
  );

  let decoratedTransaction = withReconciliations ?? transaction;
  const [withRegistrationproposal] = await decorateTransactionsWithRegistrationProposal([decoratedTransaction], {
    forceRefetch: true,
  });
  if (withRegistrationproposal) {
    decoratedTransaction = withRegistrationproposal;
  }

  const [withRecommendations] = await decorateWithRecommendedReconciliationFlows([decoratedTransaction]);

  return withRecommendations ?? decoratedTransaction;
};

const fetchTransactionsDecorated = async (
  companyId: Company['id'],
  filters: TransactionsFiltersFields,
  sorting: SortInfo<TransactionSorting>,
  pagination: PaginationInfo,
): Promise<{
  data: BookkeepingBankTransaction[];
  meta: TransactionMeta;
}> => {
  const { data: transactions, meta } = await fetchTransactions({ companyId, ...filters }, sorting, pagination, true);
  const withReconciliations = await decorateWithReconciliations(
    transactions,
    ['flow', 'flow.bookkeepingEntry', 'flow.document', 'flow.document.counterpart'],
    {
      statuses: [ReconciliationRelationStatus.VERIFIED],
    },
  );

  const withRegistrationproposals = await decorateTransactionsWithRegistrationProposal(withReconciliations);
  const withRecommendations = await decorateWithRecommendedReconciliationFlows(withRegistrationproposals);

  return { data: withRecommendations, meta };
};

const TransactionsPageSize = 20;

export const useBookkeepingBankTransactions = (
  filters: TransactionsFiltersFields,
  sorting: SortInfo<TransactionSorting>,
  options?: { enabled: boolean },
) => {
  const companyId = useActiveCompanyId();

  return useInfiniteQuery({
    queryKey: BookkeepingBankTransactionsKeys.list(companyId, filters, sorting),
    queryFn: ({ pageParam }) =>
      fetchTransactionsDecorated(companyId, filters, sorting, {
        size: TransactionsPageSize,
        cursor: pageParam,
      }),
    initialPageParam: '',
    getNextPageParam: ({
      meta: {
        page: { cursor },
      },
      data,
    }) => {
      if (data.length >= TransactionsPageSize) {
        return cursor;
      }
      return undefined;
    },
    placeholderData: keepPreviousData,
    structuralSharing: false,
    enabled: Boolean(companyId) && Boolean(options?.enabled ?? true),
  });
};

export const useBookkeepingBankTransaction = (id: Transaction['id'] | null | undefined) => {
  return useSuspenseQuery({
    queryKey: BookkeepingBankTransactionsKeys.item(id || ''),
    queryFn: () => (id ? fetchTransactionDecorated(id) : null),
    // The form reads its default values once on mount, so it must resuspend on
    // a fresh fetch every time it opens instead of initializing from a cached
    // transaction. Dropping the cache as soon as the form closes guarantees that.
    gcTime: 0,
  });
};
